import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Minus, Zap, Grid3x3, Palette, Bell, Trophy, Ban, Activity, Camera, Heart, Clock } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { captureException, addBreadcrumb, setContext } from "@/lib/sentry";
import { useQueryClient } from "@tanstack/react-query";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useProfile } from "@/hooks/useProfile";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import creditsConfig from "@/config/credits.json";
import { openInExternalBrowser, isNativeApp } from "@/lib/platform";
import { BackButton } from "@/components/ui/back-button";
import { PurchaseMethodDialog } from "@/components/PurchaseMethodDialog";
import {
  loginToRevenueCat,
  purchasePackage,
  getPackageIdForType,
  getApplePricing,
  getWebPricing,
  APPLE_PRICING_USD,
  WEB_PRICING_USD,
  type PurchaseType
} from "@/lib/purchases";
import { toast as sonnerToast } from "sonner";
import { shouldShowWebPaymentOption } from "@/lib/geolocation";
import { formatCurrency } from "@/lib/utils";

const MONTHLY_PRICE_ID = "price_1SJ0d534NhzsSHYKEgUuZ8ii";
const ANNUAL_PRICE_ID = "price_1SJ0dH34NhzsSHYKgRSud9II";
const LIFETIME_PRICE_ID = "price_1SN4QV34NhzsSHYKpPv3CtEK";
const MONTHLY_PRODUCT_ID = "prod_TFVe92KUvlKJGg";
const ANNUAL_PRODUCT_ID = "prod_TFVe3ZSPeoxlVA";
const LIFETIME_PRODUCT_ID = "prod_TJhqwUQ3IbVsuQ";

const Upgrade = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [checkingOut, setCheckingOut] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [trialAvailable, setTrialAvailable] = useState<boolean>(true);
  const [checkingTrial, setCheckingTrial] = useState<boolean>(true);

  // Check if user was redirected here due to being out of credits
  const fromCredits = !!(location.state as any)?.fromCredits;

  // Purchase method dialog state
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState<{
    type: PurchaseType;
    priceId: string;
    isLifetime?: boolean;
  } | null>(null);
  const [showWebOption, setShowWebOption] = useState(true);

  // App Store subscription warning dialog
  const [showAppStoreWarning, setShowAppStoreWarning] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState<{
    priceId: string;
    isLifetime: boolean;
  } | null>(null);

  useEffect(() => {
    // Mark that the user has visited the upgrade page (used to show credit counter on Track)
    localStorage.setItem("hasVisitedUpgrade", "true");

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate("/auth");
      } else {
        setUser(session.user);
        // Auto-check subscription on page load (user might be returning from Stripe)
        setTimeout(() => {
          handleRefreshSubscription();
        }, 1000);
        // Check trial availability
        checkTrialAvailability();
      }
      setLoading(false);
    });
  }, [navigate]);

  // Use centralized profile hook for username check
  const { data: userProfile, isLoading: isLoadingUserProfile } = useProfile(user?.id);

  // Only redirect if profile is loaded and has no username
  useEffect(() => {
    if (loading || !user || isLoadingUserProfile) return;

    // IMPORTANT: Only redirect if we have a loaded profile that definitely has no username
    // If profile is undefined or null, it might just be loading/refetching - don't redirect
    if (userProfile && userProfile.username === null) {
      console.log("[Upgrade] No username found (username is null), redirecting to auth for onboarding");
      localStorage.removeItem("onboarding_complete");
      navigate("/auth", { replace: true });
    }
  }, [loading, user, userProfile, isLoadingUserProfile, navigate]);

  const checkTrialAvailability = async () => {
    setCheckingTrial(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();

      const { data, error } = await supabase.functions.invoke("check-trial-availability", {
        headers: {
          Authorization: `Bearer ${sessionData.session?.access_token}`,
        },
      });

      if (error) {
        console.error("Error checking trial availability:", error);
        // Default to trial available on error
        setTrialAvailable(true);
      } else {
        setTrialAvailable(data?.trialAvailable ?? true);
      }
    } catch (error) {
      console.error("Error checking trial availability:", error);
      captureException(error, {
        tags: { page: 'upgrade', action: 'check-trial' },
        extra: { userId: user?.id },
      });
      // Default to trial available on error
      setTrialAvailable(true);
    } finally {
      setCheckingTrial(false);
    }
  };

  // Use centralized subscription status
  const { isPremium, subscriptionProductId, isLoading: isLoadingSubscription } = useSubscriptionStatus(user?.id);

  // Map subscription product ID to our constants for comparison
  const currentProductId = subscriptionProductId;

  const handleRefreshSubscription = async () => {
    if (!user?.id) return;

    setRefreshing(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();

      // Call check-subscription ONE TIME for immediate feedback
      // Note: Subscription is also synced globally every minute by scheduled job
      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: {
          Authorization: `Bearer ${sessionData.session?.access_token}`,
        },
      });

      if (error) throw error;

      const wasPremium = isPremium;

      // The check-subscription function already updates the DB, just refetch our queries
      if (data) {
        // Try to grant daily credits immediately for premium users
        if (data.subscribed) {
          await supabase.functions.invoke("grant-daily-credits", {
            headers: { Authorization: `Bearer ${sessionData.session?.access_token}` },
          }).catch(e => {
            console.error('[Upgrade] Credits grant failed:', e);
          });
        }

        // If just became premium, show celebration
        if (data.subscribed && !wasPremium) {
          toast({
            title: t('upgrade.toast.welcome_premium'),
            description: t('upgrade.toast.account_upgraded', { credits: creditsConfig.premium.dailyRefillCredits }),
            duration: 6000,
          });
        } else if (data.subscribed) {
          toast({
            title: t('upgrade.toast.premium_active'),
            description: t('upgrade.toast.subscription_active'),
          });
        } else {
          toast({
            title: t('upgrade.toast.status_updated'),
            description: t('upgrade.toast.status_refreshed'),
          });
        }

        // Refetch subscription status and credits
        queryClient.invalidateQueries({ queryKey: ["subscription-status", user.id] });
        queryClient.invalidateQueries({ queryKey: ["user-credits", user.id] });
        queryClient.invalidateQueries({ queryKey: ["profile", user.id] });
      }
    } catch (error: any) {
      captureException(error, {
        tags: { page: 'upgrade', action: 'refresh-subscription' },
        extra: { userId: user?.id },
      });
      toast({
        title: t('upgrade.toast.refresh_error'),
        description: error.message || t('upgrade.toast.refresh_failed'),
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleCheckout = async (priceId: string, purchaseType: PurchaseType) => {
    // On native devices, show purchase method dialog
    if (isNativeApp()) {
      // Check geolocation to determine if web option should be shown
      const showWeb = await shouldShowWebPaymentOption();
      setShowWebOption(showWeb);

      setPendingPurchase({ type: purchaseType, priceId, isLifetime: false });
      setPurchaseDialogOpen(true);
      return;
    }

    // On desktop web, check if user has an App Store subscription
    const hasAppStoreSubscription = subscriptionProductId?.startsWith('revenuecat_');
    if (hasAppStoreSubscription) {
      // Show warning dialog asking user to cancel App Store subscription first
      setPendingCheckout({ priceId, isLifetime: false });
      setShowAppStoreWarning(true);
      return;
    }

    // On web, proceed directly with Stripe
    await handleWebCheckout(priceId);
  };

  const handleWebCheckout = async (priceId: string) => {
    setCheckingOut(priceId);
    try {
      const { data: sessionData } = await supabase.auth.getSession();

      // Use trial only if available and user is not already premium
      const shouldUseTrial = trialAvailable && !isPremium;

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId, trial: shouldUseTrial },
        headers: {
          Authorization: `Bearer ${sessionData.session?.access_token}`,
        },
      });

      if (error) {
        // Handle 403 trial already used error
        if (error.message?.includes("Trial already used") || data?.error) {
          toast({
            title: t('upgrade.toast.trial_unavailable'),
            description: data?.error || error.message || t('upgrade.toast.trial_already_used'),
            variant: "destructive",
          });
          return;
        }
        throw error;
      }

      // Check if error response exists
      if (data?.error) {
        // Check if it's an active subscription error (downgrade/duplicate attempt)
        const isSubscriptionError = data?.hasActiveSubscription ||
          data.error.includes("active subscription");

        toast({
          title: isSubscriptionError ? t('upgrade.toast.already_subscribed') : t('upgrade.toast.trial_unavailable'),
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      if (data.url) {
        // Open Stripe checkout in external browser (Safari) to comply with Apple's App Store policy
        const result = await openInExternalBrowser(data.url);

        if (!result.success) {
          toast({
            title: t('upgrade.toast.safari_error'),
            description: result.error || t('upgrade.toast.checkout_open_failed'),
            variant: "destructive",
          });
        }
      }
    } catch (error: any) {
      // Parse the error message - check if it's a structured error response
      const errorMsg = error.message || "Failed to create checkout session";
      const isTrialError = errorMsg.includes("Trial already used") || errorMsg.includes("trial");

      toast({
        title: isTrialError ? t('upgrade.toast.trial_unavailable') : t('upgrade.toast.checkout_error'),
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setCheckingOut(null);
      setPurchaseDialogOpen(false);
    }
  };

  const handleLifetimeCheckout = async (priceId: string) => {
    // On native devices, show purchase method dialog
    if (isNativeApp()) {
      // Check geolocation to determine if web option should be shown
      const showWeb = await shouldShowWebPaymentOption();
      setShowWebOption(showWeb);

      setPendingPurchase({ type: "lifetime", priceId, isLifetime: true });
      setPurchaseDialogOpen(true);
      return;
    }

    // On web, lifetime purchases can proceed regardless of App Store subscription
    // (lifetime overrides any recurring subscription)
    await handleWebLifetimeCheckout(priceId);
  };

  const handleWebLifetimeCheckout = async (priceId: string) => {
    setCheckingOut(priceId);
    try {
      const { data: sessionData } = await supabase.auth.getSession();

      const { data, error } = await supabase.functions.invoke("create-lifetime-checkout", {
        body: { priceId },
        headers: {
          Authorization: `Bearer ${sessionData.session?.access_token}`,
        },
      });

      if (error) throw error;

      if (data?.error) {
        toast({
          title: t('upgrade.toast.checkout_error'),
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      if (data.url) {
        // Open Stripe checkout in external browser (Safari) to comply with Apple's App Store policy
        const result = await openInExternalBrowser(data.url);

        if (!result.success) {
          toast({
            title: t('upgrade.toast.safari_error'),
            description: result.error || t('upgrade.toast.checkout_open_failed'),
            variant: "destructive",
          });
        }
      }
    } catch (error: any) {
      captureException(error, {
        tags: { page: 'upgrade', action: 'stripe-checkout' },
        extra: {
          userId: user?.id,
          checkingOut: checkingOut,
          isNative: isNativeApp(),
        },
      });
      toast({
        title: t('upgrade.toast.checkout_error'),
        description: error.message || t('upgrade.toast.checkout_failed'),
        variant: "destructive",
      });
    } finally {
      setCheckingOut(null);
      setPurchaseDialogOpen(false);
    }
  };

  const handleApplePurchase = async () => {
    if (!pendingPurchase || !user) {
      sonnerToast.error(t('upgrade.toast.purchase_info_missing'));
      return;
    }

    setCheckingOut(pendingPurchase.priceId);
    setPurchaseDialogOpen(false);

    try {
      // WARNING: RevenueCat login() can transfer subscriptions between users
      // This is a known limitation - we rely on server-side validation to revoke stolen subscriptions
      const loginResult = await loginToRevenueCat(user.id, user.email || "", supabase);
      if (!loginResult.success) {
        if (loginResult.error?.includes('another account')) {
          sonnerToast.error(t('upgrade.toast.subscription_linked_error'), {
            description: t('upgrade.toast.subscription_no_transfer')
          });
          return;
        }
        throw new Error(loginResult.error || "Failed to login to RevenueCat");
      }

      console.log('[Upgrade] Logged into RevenueCat:', loginResult.customerId);

      // Get the package ID for the purchase type
      const packageId = getPackageIdForType(pendingPurchase.type);
      if (!packageId) {
        throw new Error("Invalid purchase type");
      }

      // Purchase the package
      const toastId = sonnerToast.loading(t('upgrade.toast.please_wait'));
      const purchaseResult = await purchasePackage(packageId);

      // Dismiss the loading toast once the dialog completes
      sonnerToast.dismiss(toastId);

      if (!purchaseResult.success) {
        console.error('[Upgrade] Purchase failed with details:', {
          error: purchaseResult.error,
          packageId,
          type: pendingPurchase.type,
          fullResult: JSON.stringify(purchaseResult, null, 2),
        });
        throw new Error(purchaseResult.error || "Purchase failed");
      }

      // Immediately verify the subscription is valid (not stolen)
      const verifyToast = sonnerToast.loading(t('upgrade.toast.verifying_subscription'));

      try {
        await handleRefreshSubscription();
        sonnerToast.dismiss(verifyToast);
        sonnerToast.success(t('upgrade.toast.purchase_successful'));

        // Navigate to home screen after successful purchase
        navigate("/");
      } catch (verifyError: any) {
        console.error('[Upgrade] Verification error:', {
          error: verifyError,
          message: verifyError?.message,
          stack: verifyError?.stack,
        });
        sonnerToast.dismiss(verifyToast);
        sonnerToast.error(t('upgrade.toast.verification_failed'), {
          description: t('upgrade.toast.verification_failed_desc')
        });
      }
    } catch (error: any) {
      console.error('[Upgrade] Apple purchase error:', error);
      sonnerToast.error(error.message || "Failed to complete Apple purchase");
    } finally {
      setCheckingOut(null);
      setPendingPurchase(null);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();

      const { data, error } = await supabase.functions.invoke("customer-portal", {
        headers: {
          Authorization: `Bearer ${sessionData.session?.access_token}`,
        },
      });

      if (error) throw error;
      if (data.url) {
        // Open Stripe checkout in external browser (Safari) to comply with Apple's App Store policy
        const result = await openInExternalBrowser(data.url);

        if (!result.success) {
          toast({
            title: t('upgrade.toast.safari_error'),
            description: result.error || t('upgrade.toast.portal_open_failed'),
            variant: "destructive",
          });
        }
      }
    } catch (error: any) {
      toast({
        title: t('upgrade.toast.error'),
        description: error.message || "Failed to open customer portal",
        variant: "destructive",
      });
    }
  };

  const features = [
    {
      name: t('upgrade.features.unlimited_ai'),
      icon: Zap,
      free: "",
      pro: "",
      showChart: false,
    },
    {
      name: t('upgrade.features.custom_metrics'),
      icon: Grid3x3,
      free: t('upgrade.features.preview_period'),
      pro: "",
      showChart: false,
    },
    {
      name: t('upgrade.features.progress_snaps'),
      subtitle: t('upgrade.features.snaps_subtitle'),
      icon: Camera,
      free: "",
      pro: "",
      showChart: false,
    },
    {
      name: t('upgrade.features.chart_colors'),
      icon: Palette,
      free: t('upgrade.features.green_only'),
      pro: t('upgrade.features.any_color'),
      showChart: true,
    },
    {
      name: t('upgrade.features.reminders'),
      icon: Bell,
      free: "",
      pro: t('upgrade.features.reminder_interval'),
      showChart: false,
    },
    {
      name: t('upgrade.features.leaderboard'),
      icon: Trophy,
      free: "",
      pro: t('upgrade.features.full_access'),
      showChart: false,
    },
  ];

  return (
    <motion.div
      className="min-h-screen bg-background p-4 relative"
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      transition={{ type: "spring", damping: 30, stiffness: 300 }}
    >
      {/* Background gradient effect - only in dark mode */}
      <div className="dark:block hidden">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 2.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 pointer-events-none overflow-hidden will-change-[opacity]"
          style={{ top: '15%' }}
        >
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[150%] h-[85%] bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-purple-600/20 via-blue-500/10 to-transparent blur-[100px]" />
        </motion.div>
      </div>

      {/* Limited-time offer banner at top */}
      <div className="flex justify-center pt-14 pb-2">
        <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-orange-500/20 to-amber-500/20 border border-orange-500/30 text-orange-600 dark:text-orange-400 px-4 py-1.5 rounded-full text-xs font-semibold shadow-sm">
          <Clock className="h-3.5 w-3.5" />
          {t('upgrade.header.limited_time_offer')}
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="space-y-5 pt-2 pb-4">
          {/* Header */}
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold">{t('upgrade.header.title')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('upgrade.header.subtitle')}
            </p>
          </div>

          {/* Two-Column Comparison */}
          <div className="space-y-3">
            {/* Headers */}
            <div className="grid grid-cols-[1.5fr,1fr] gap-2 px-3 sm:gap-4 sm:px-4">
              <div className="font-semibold text-sm sm:text-base">{t('upgrade.table.feature')}</div>
              <div className="text-center font-semibold text-sm sm:text-base text-accent">{t('upgrade.table.pro')}</div>
            </div>

            {/* Feature Rows */}
            {features.map((feature, index) => (
              <Card key={index} className="p-3 sm:p-4 border">
                <div className="grid grid-cols-[1.5fr,1fr] gap-2 sm:gap-4 items-center">
                  <div className="flex items-center gap-2">
                    <feature.icon className="h-4 w-4 text-accent flex-shrink-0" />
                    <div className="flex flex-col">
                      <h3 className="font-medium text-xs sm:text-sm leading-tight">{feature.name}</h3>
                      {feature.subtitle && <span className="text-[10px] text-muted-foreground">{feature.subtitle}</span>}
                    </div>
                  </div>

                  {/* Pro Column */}
                  <div className="text-center flex justify-center items-center">
                    {feature.showChart ? (
                      <div className="flex justify-center gap-2">
                        <div className="flex gap-0.5">
                          {Array.from({ length: 8 }).map((_, week) => {
                            const rainbowColors = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#06b6d4", "#3b82f6", "#8b5cf6", "#ec4899"];
                            return (
                              <div key={week} className="flex flex-col gap-0.5">
                                {Array.from({ length: 3 }).map((_, day) => (
                                  <div
                                    key={day}
                                    className="w-1.5 h-1.5 rounded-[1px]"
                                    style={{
                                      backgroundColor: rainbowColors[week]
                                    }}
                                  />
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-1">
                        <Check className="h-5 w-5 text-accent flex-shrink-0" />
                        {feature.pro && <span className="text-[10px] sm:text-xs font-medium text-accent leading-tight">{feature.pro}</span>}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Pricing and CTA Buttons */}
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <Card className="p-4 border-2 border-accent/40 !bg-transparent">
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm">{t('upgrade.plans.monthly')}</h3>
                  <div className="space-y-0.5">
                    {isNativeApp() ? (
                      <>
                        <div className="text-lg font-bold">{formatCurrency(APPLE_PRICING_USD.MONTHLY)}<span className="text-[10px] text-muted-foreground">{t('upgrade.plans.per_month')}</span></div>
                        {/* <div className="text-[11px] text-muted-foreground">or {formatCurrency(WEB_PRICING_USD.MONTHLY)}/mo web</div> */}
                      </>
                    ) : (
                      <div className="text-xl font-bold">{formatCurrency(WEB_PRICING_USD.MONTHLY)}<span className="text-xs text-muted-foreground">{t('upgrade.plans.per_month')}</span></div>
                    )}
                  </div>
                  {trialAvailable && !isPremium && (
                    <p className="text-xs text-muted-foreground">{t('upgrade.plans.trial_days')}</p>
                  )}
                  <Button
                    onClick={() => handleCheckout(MONTHLY_PRICE_ID, "monthly")}
                    disabled={checkingOut === MONTHLY_PRICE_ID || currentProductId === MONTHLY_PRODUCT_ID || checkingTrial}
                    className="w-full bg-gradient-to-r from-accent to-accent/80 hover:from-accent/90 hover:to-accent/70"
                    size="sm"
                  >
                    {currentProductId === MONTHLY_PRODUCT_ID
                      ? t('upgrade.actions.current_plan')
                      : (trialAvailable && !isPremium)
                        ? t('upgrade.actions.start_trial')
                        : t('upgrade.actions.subscribe')}
                  </Button>
                </div>
              </Card>


              <Card className="p-4 border-2 border-accent relative !bg-transparent">
                <div className="absolute -top-2 right-2 bg-green-600 px-2 py-0.5 rounded-full">
                  <span className="text-white text-xs font-bold">{isNativeApp() ? t('upgrade.plans.discount_51') : t('upgrade.plans.discount_58')}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm">{t('upgrade.plans.annual')}</h3>
                    <span className="text-[11px] text-muted-foreground">
                      ({isNativeApp() ? formatCurrency(APPLE_PRICING_USD.ANNUAL / 12) : formatCurrency(WEB_PRICING_USD.ANNUAL / 12)}{t('upgrade.plans.per_month')})
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {isNativeApp() ? (
                      <>
                        <div className="text-lg font-bold">{formatCurrency(APPLE_PRICING_USD.ANNUAL)}<span className="text-[10px] text-muted-foreground">{t('upgrade.plans.per_year')}</span></div>
                        {/* <div className="text-[11px] text-muted-foreground">or {formatCurrency(WEB_PRICING_USD.ANNUAL)}/yr web</div> */}
                      </>
                    ) : (
                      <>
                        <div className="text-xl font-bold">{formatCurrency(WEB_PRICING_USD.ANNUAL)}<span className="text-xs text-muted-foreground">{t('upgrade.plans.per_year')}</span></div>
                        <span className="text-xs text-muted-foreground line-through">{formatCurrency(WEB_PRICING_USD.MONTHLY * 12)}{t('upgrade.plans.per_year')}</span>
                      </>
                    )}
                  </div>
                  {trialAvailable && !isPremium && (
                    <p className="text-xs text-muted-foreground">{t('upgrade.plans.trial_days')}</p>
                  )}
                  <Button
                    onClick={() => handleCheckout(ANNUAL_PRICE_ID, "annual")}
                    disabled={checkingOut === ANNUAL_PRICE_ID || currentProductId === ANNUAL_PRODUCT_ID || checkingTrial}
                    className="w-full bg-gradient-to-r from-accent to-accent/80 hover:from-accent/90 hover:to-accent/70"
                    size="sm"
                  >
                    {currentProductId === ANNUAL_PRODUCT_ID
                      ? t('upgrade.actions.current_plan')
                      : currentProductId === MONTHLY_PRODUCT_ID
                        ? t('upgrade.actions.upgrade')
                        : (trialAvailable && !isPremium)
                          ? t('upgrade.actions.start_trial')
                          : t('upgrade.actions.subscribe')}
                  </Button>
                </div>
              </Card>

            </div >

            <Card className="p-4 border-2 border-purple-500 relative !bg-transparent">
              <div className="absolute -top-2 right-2 bg-purple-600 px-2 py-0.5 rounded-full">
                <span className="text-white text-xs font-bold">{t('upgrade.plans.best_value')}</span>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-sm">{t('upgrade.plans.lifetime')}</h3>
                <div className="space-y-0.5">
                  {isNativeApp() ? (
                    <>
                      <div className="text-lg font-bold">{formatCurrency(APPLE_PRICING_USD.LIFETIME)}<span className="text-[10px] text-muted-foreground"> {t('upgrade.plans.once')}</span></div>
                      {/* <div className="text-[11px] text-muted-foreground">or {formatCurrency(WEB_PRICING_USD.LIFETIME)} web</div> */}
                    </>
                  ) : (
                    <div className="text-xl font-bold">{formatCurrency(WEB_PRICING_USD.LIFETIME)}<span className="text-xs text-muted-foreground"> {t('upgrade.plans.once')}</span></div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{t('upgrade.plans.forever_access')}</p>
                <Button
                  onClick={() => handleLifetimeCheckout(LIFETIME_PRICE_ID)}
                  disabled={checkingOut === LIFETIME_PRICE_ID || currentProductId === LIFETIME_PRODUCT_ID}
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800"
                  size="sm"
                >
                  {currentProductId === LIFETIME_PRODUCT_ID
                    ? t('upgrade.actions.current_plan')
                    : (currentProductId === MONTHLY_PRODUCT_ID || currentProductId === ANNUAL_PRODUCT_ID)
                      ? t('upgrade.actions.upgrade')
                      : t('upgrade.actions.get_lifetime')}
                </Button>
              </div>
            </Card>
          </div >

          <p className="text-xs text-center text-muted-foreground">
            {t('upgrade.footer.cancel_anytime')}
          </p>

          {/* Indie Developer Support Bubble */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="p-4 border border-pink-500/30 bg-gradient-to-r from-pink-500/5 to-purple-500/5">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-pink-500/10 rounded-full">
                  <Heart className="h-5 w-5 text-pink-500 fill-pink-500" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">{t('upgrade.indie_support.title')}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {t('upgrade.indie_support.description')}
                  </p>
                  <button 
                    onClick={() => navigate('/about')}
                    className="text-xs font-medium text-pink-500 hover:text-pink-600 hover:underline"
                  >
                    {t('upgrade.indie_support.read_more')} →
                  </button>
                </div>
              </div>
            </Card>
          </motion.div>

          {
            isPremium && (
              <Card className="p-4 bg-muted border text-center">
                <p className="text-sm font-medium mb-2">{t('upgrade.status.already_premium')}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/settings", { state: { expandAccount: true } })}
                >
                  {t('upgrade.actions.manage_subscription')}
                </Button>
              </Card>
            )
          }


        </div >
      </div >

      {/* Purchase Method Dialog for Native Devices */}
      {
        pendingPurchase && (
          <PurchaseMethodDialog
            open={purchaseDialogOpen}
            onOpenChange={setPurchaseDialogOpen}
            title={t('upgrade.dialog.subscription_title', { type: pendingPurchase.type })}
            webPrice={getWebPricing(pendingPurchase.type)}
            applePrice={getApplePricing(pendingPurchase.type)}
            onWebPurchase={() => {
              if (pendingPurchase.isLifetime) {
                handleWebLifetimeCheckout(pendingPurchase.priceId);
              } else {
                handleWebCheckout(pendingPurchase.priceId);
              }
            }}
            onApplePurchase={handleApplePurchase}
            isProcessing={checkingOut !== null}
            showWebOption={showWebOption}
            currentSubscriptionProductId={subscriptionProductId}
            isPremium={isPremium}
            isForCredits={false}
            trialAvailable={trialAvailable}
          />
        )
      }

      {/* App Store Subscription Warning Dialog for Desktop Users */}
      <AlertDialog open={showAppStoreWarning} onOpenChange={setShowAppStoreWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('upgrade.warning.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('upgrade.warning.description')}
              <br /><br />
              <strong>{t('upgrade.warning.instructions_title')}</strong>
              <ol className="list-decimal list-inside space-y-1 mt-2">
                <li>{t('upgrade.warning.step1')}</li>
                <li>{t('upgrade.warning.step2')}</li>
                <li>{t('upgrade.warning.step3')}</li>
                <li>{t('upgrade.warning.step4')}</li>
                <li>{t('upgrade.warning.step5')}</li>
              </ol>
              <br />
              {t('upgrade.warning.final_note')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.got_it')}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div >
  );
};

export default Upgrade;
